import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RegulationStatus } from '@vendasta/protos/sms/v2/enums';
import type { RegulationInfo } from '@vendasta/protos/sms/v2/types';
import { map, Observable } from 'rxjs';
import { ProgressTrackerComponent, StageStatus, Status } from '../../progress-tracker/progress-tracker.component';
import { RegulationService } from '../regulation.service';

const STAGES_KEY = 'SMS_REGULATION.COMMON.STATUS.STAGES';
const RESOURCE_TYPE_KEY = 'SMS_REGULATION.COMMON.STATUS.RESOURCE_TYPE';
const TWILIO_ERROR_KEY = 'SMS_REGULATION.COMMON.STATUS.TWILIO_ERROR';

@Component({
  selector: 'sms-regulatory-status',
  imports: [AsyncPipe, MatCardModule, GalaxyAlertModule, ProgressTrackerComponent, TranslateModule],
  templateUrl: './sms-regulatory-status.component.html',
  styleUrl: './sms-regulatory-status.component.scss',
})
export class SmsRegulatoryStatusComponent {
  private readonly regulationService = inject(RegulationService);
  private readonly translate = inject(TranslateService);

  protected readonly regulationInfo$: Observable<RegulationInfo | null> = this.regulationService.regulationInfo$;

  protected readonly stages$: Observable<StageStatus[]> = this.regulationInfo$.pipe(
    map((info: RegulationInfo | null) => this.buildStages(info)),
  );

  protected readonly RegulationStatus = RegulationStatus;

  protected resourceTypeLabel(resourceType: string): string {
    if (!resourceType) {
      return this.translate.instant(`${RESOURCE_TYPE_KEY}.UNKNOWN`);
    }
    const key = `${RESOURCE_TYPE_KEY}.${resourceType.toUpperCase()}`;
    const translated = this.translate.instant(key);
    return translated === key ? resourceType : translated;
  }

  protected rejectionMessage(errorCodes: readonly number[] | undefined, fallback: string): string {
    if (!errorCodes?.length) {
      return fallback;
    }
    const translated = errorCodes
      .map((code) => {
        const key = `${TWILIO_ERROR_KEY}.${code}`;
        const value = this.translate.instant(key);
        return value === key ? null : value;
      })
      .filter((s): s is string => s !== null);
    return translated.length > 0 ? translated.join(' ') : fallback;
  }

  private stageLabel(key: string): string {
    return this.translate.instant(`${STAGES_KEY}.${key}`);
  }

  private buildStages(info: RegulationInfo | null): StageStatus[] {
    if (!info) {
      return [
        { name: this.stageLabel('SUBMIT_INFORMATION'), status: Status.NotStarted },
        { name: this.stageLabel('REVIEW'), status: Status.NotStarted },
        { name: this.stageLabel('APPROVED'), status: Status.NotStarted },
      ];
    }

    switch (info.status) {
      case RegulationStatus.REGULATION_STATUS_APPROVED:
        return [
          { name: this.stageLabel('INFORMATION_SUBMITTED'), status: Status.Completed },
          { name: this.stageLabel('REVIEW'), status: Status.Completed },
          { name: this.stageLabel('APPROVED'), status: Status.Completed },
        ];
      case RegulationStatus.REGULATION_STATUS_REJECTED:
        return [
          { name: this.stageLabel('INFORMATION_SUBMITTED'), status: Status.Completed },
          { name: this.stageLabel('REVIEW'), status: Status.Failed },
          { name: this.stageLabel('REJECTED'), status: Status.Failed },
        ];
      case RegulationStatus.REGULATION_STATUS_PENDING_REVIEW:
      default:
        return [
          { name: this.stageLabel('INFORMATION_SUBMITTED'), status: Status.Completed },
          { name: this.stageLabel('REVIEW'), status: Status.InProgress },
          { name: this.stageLabel('APPROVED'), status: Status.NotStarted },
        ];
    }
  }
}
